import * as React from 'react';
import propz from 'propz';
import { History } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Permission } from '../../../../models/permission';
import { PROFILE_TABS_URLS } from '../../../../consts/profile';
import { getPermissions, deletePermission } from 'Src/helpers/service/admin/user';
import { SimpleModal } from '../../../../components/SimpleModal/SimpleModal';
import { Loader } from '../../../../components/Loader/Loader';
import './RolesTab.scss';
import { ROLE } from '../../../../consts/user';

interface Props {
  user: AppUser;
  history: History;
  updateIsRolesExist?: (boolean) => void;
}

interface State {
  permissions: Permission[];
  permissionToDelete: Permission;
  isLoading: boolean;
  isConfirmationModalOpen: boolean;
  permissionToDetailsView: Permission;
  isConfirmationModalDetailsViewOpen: boolean;
  isRemoveErrorModalOpen: boolean;
  schoolSearch: string;
  schoolAppliedSearch: string;
}

export class RolesTab extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      permissions: [],
      permissionToDelete: undefined,
      isLoading: false,
      isConfirmationModalOpen: false,
      permissionToDetailsView: undefined,
      isConfirmationModalDetailsViewOpen: false,
      isRemoveErrorModalOpen: false,
      schoolSearch: '',
      schoolAppliedSearch: ''
    };
  }

  componentDidMount() {
    const { user } = this.props;
    this.setState({ isLoading: true });

    const filter = {
      limit: 250,
      skip: 0
    };

    getPermissions(user, filter).then(permissions => {
      this.setState({
        permissions,
        isLoading: false
      });
    });
  }

  getRoleLabel = (preset: string): string => {
    if (preset === ROLE.TOURNAMENT_FOLLOWER) return 'Tournament follower';
    if (!preset) return '';
    return preset.charAt(0).toUpperCase() + preset.slice(1).toLowerCase();
  };

  getRoleBadgeClass = (preset: string): string => {
    switch (preset) {
      case ROLE.ADMIN:
        return 'upRoleBadge upRoleBadgeInfo';
      case ROLE.PARENT:
        return 'upRoleBadge upRoleBadgeSuccess';
      case ROLE.STUDENT:
        return 'upRoleBadge upRoleBadgeNeutral';
      default:
        return 'upRoleBadge upRoleBadgeWarning';
    }
  };

  getSchoolInitials = (name: string): string => {
    return String(name || 'School')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('');
  };

  getPermissionSports = (permission: Permission): any[] => {
    const sports = propz.get(permission, ['sports'], []);
    const seen = new Set<string>();

    return sports.filter(sport => {
      const key = String(sport.id || sport.name || '').toLowerCase();
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  getLinkedStudentName = (student): string => {
    return [student.firstName, student.lastName].filter(Boolean).join(' ') || 'Linked child';
  };

  renderPermissionDetails = (permission: Permission): React.ReactNode => {
    const isCoachOrTeacher = permission.preset === ROLE.COACH || permission.preset === ROLE.TEACHER;

    if (isCoachOrTeacher) {
      const sports = this.getPermissionSports(permission);
      const visibleSports = sports.slice(0, 3);
      const hiddenSportsCount = Math.max(0, sports.length - visibleSports.length);

      if (sports.length === 0) {
        return <span className="upRolesDetailMuted">All activities / sports</span>;
      }

      return (
        <div className="upRolesDetailChips">
          {visibleSports.map(sport => (
            <span className="upRolesDetailChip" key={sport.id || sport.name}>
              {sport.name}
            </span>
          ))}
          {hiddenSportsCount > 0 && (
            <button
              type="button"
              className="upRolesDetailMore"
              onClick={() => this.onDetailsViewClick(permission)}
              aria-label={`Show all ${sports.length} activities and sports`}
            >
              +{hiddenSportsCount}
            </button>
          )}
        </div>
      );
    }

    if (permission.preset === ROLE.PARENT) {
      const linkedStudents = permission.linkedStudents || [];

      if (linkedStudents.length === 0) {
        return <span className="upRolesDetailMuted">No linked children</span>;
      }

      return (
        <div className="upRolesLinkedChildren">
          {linkedStudents.map(student => (
            <span className="upRolesChild" key={student.id}>
              <span className="upRolesChildAvatar" aria-hidden="true">
                {`${student.firstName?.charAt(0) || ''}${student.lastName?.charAt(0) || ''}`.toUpperCase() || 'C'}
              </span>
              <span>{this.getLinkedStudentName(student)}</span>
            </span>
          ))}
        </div>
      );
    }

    return <span className="upRolesDetailMuted">—</span>;
  };

  onDetailsViewClick = (permission: Permission): void => {
    this.setState({
      permissionToDetailsView: permission,
      isConfirmationModalDetailsViewOpen: true
    });
  };

  onOkConfirmationModalDetailsViewClick = () => {
    this.setState({ isConfirmationModalDetailsViewOpen: false });
  };

  renderConfirmationModalDetailsView = () => {
    const { isConfirmationModalDetailsViewOpen, permissionToDetailsView } = this.state;
    const sports = permissionToDetailsView ? this.getPermissionSports(permissionToDetailsView) : [];
    const schoolName = propz.get(permissionToDetailsView, ['school', 'name'], '');

    return (
      <SimpleModal
        isOpen={isConfirmationModalDetailsViewOpen}
        title={'Allowed activities / sports'}
        body={
          <div className="upRolesSportsModalBody">
            {schoolName && <div className="upRolesSportsModalSchool">{schoolName}</div>}
            {sports.length > 0 ? (
              <div className="upRolesDetailChips">
                {sports.map(sport => (
                  <span className="upRolesDetailChip" key={sport.id || sport.name}>
                    {sport.name}
                  </span>
                ))}
              </div>
            ) : (
              <div className="upRolesDetailMuted">All activities / sports are allowed.</div>
            )}
          </div>
        }
        buttonText={'Close'}
        onButtonClick={this.onOkConfirmationModalDetailsViewClick}
        onCloseIconClick={this.onOkConfirmationModalDetailsViewClick}
        isBodyBold={false}
        customClass={'upProfileCompactModal'}
      />
    );
  };

  onRevokeClick = (permission: Permission): void => {
    const isStudentRole = permission.preset === ROLE.STUDENT;

    if (isStudentRole) {
      this.setState({ isRemoveErrorModalOpen: true });
    } else {
      this.setState({
        permissionToDelete: permission,
        isConfirmationModalOpen: true
      });
    }
  };

  onOkConfirmationModalClick = (): void => {
    const { user } = this.props;
    const { permissionToDelete } = this.state;
    this.setState({ isLoading: true });

    deletePermission(user, permissionToDelete.id).then(() => {
      const { permissions } = this.state;
      const permissionsUpdated = permissions.filter(permission => permission.id !== permissionToDelete.id);
      this.setState({
        isLoading: false,
        permissions: permissionsUpdated,
        isConfirmationModalOpen: false
      });
      if (permissionsUpdated.length === 0) {
        const { updateIsRolesExist } = this.props;
        updateIsRolesExist(false);
      }
    });
  };

  onCancelConfirmationModalClick = (): void => {
    this.setState({
      permissionToDelete: undefined,
      isConfirmationModalOpen: false
    });
  };

  renderConfirmationModal(): React.ReactNode {
    const { isConfirmationModalOpen, permissionToDelete } = this.state;
    const role = this.getRoleLabel(propz.get(permissionToDelete, ['preset'], ''));
    const schoolName = propz.get(permissionToDelete, ['school', 'name'], 'this school');
    const linkedStudents = propz.get(permissionToDelete, ['linkedStudents'], []);

    return (
      <SimpleModal
        isOpen={isConfirmationModalOpen}
        title={`Revoke ${role || 'role'}?`}
        body={
          <div className="upRolesRevokeBody">
            <p>
              You are about to revoke your <strong>{role}</strong> role at <strong>{schoolName}</strong>.
            </p>
            <p>
              You will lose the access granted by this role and will no longer be able to switch to it or use its school
              features.
            </p>
            {linkedStudents.length > 0 && (
              <p>
                This role is linked to{' '}
                <strong>{linkedStudents.map(student => this.getLinkedStudentName(student)).join(', ')}</strong>.
              </p>
            )}
            <p>
              If you need this access again, you will have to submit a new role request and wait for the school to
              approve it.
            </p>
          </div>
        }
        buttonText={'Revoke role'}
        onButtonClick={this.onOkConfirmationModalClick}
        buttonCancelText={'Cancel'}
        onCloseClick={this.onCancelConfirmationModalClick}
        isBodyBold={false}
        customClass={'upProfileCompactModal upProfileDangerModal'}
      />
    );
  }

  onRequestsTabClick = (): void => {
    this.props.history.push(PROFILE_TABS_URLS.REQUESTS);
  };

  renderRemoveErrorModal(): React.ReactNode {
    const { isRemoveErrorModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isRemoveErrorModalOpen}
        title={'Student role'}
        body={`You can't delete a student role.`}
        buttonCancelText={'Close'}
        onCloseClick={this.closeRemoveErrorModal}
        customClass={'upProfileCompactModal'}
      />
    );
  }

  closeRemoveErrorModal = () => {
    this.setState({ isRemoveErrorModalOpen: false });
  };

  render() {
    const { permissions, isLoading, schoolSearch, schoolAppliedSearch } = this.state;
    const isPermissionsExist = permissions.length > 0;
    const normalizedSearch = schoolAppliedSearch.trim().toLowerCase();
    const visiblePermissions = normalizedSearch
      ? permissions.filter(permission => {
          const schoolName = propz.get(permission, ['school', 'name'], '');
          return String(schoolName)
            .toLowerCase()
            .includes(normalizedSearch);
        })
      : permissions;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="upRolesTab">
        {this.renderConfirmationModal()}
        {this.renderConfirmationModalDetailsView()}
        {this.renderRemoveErrorModal()}

        <div className="upProfileInfo">
          <span className="upProfileInfoIcon">i</span>
          <div>
            <div>Roles you hold across the platform. Revoke any you no longer need.</div>
            <div>Switch between roles from the avatar menu. Missing a role? It may still be awaiting approval.</div>
          </div>
        </div>

        <section className="upProfileCard">
          <header className="upProfileCardHeader">
            <div>
              <h2 className="upProfileCardTitle">Your roles</h2>
              <p className="upProfileCardSub">Schools and access levels currently linked to your account.</p>
            </div>
            {isPermissionsExist && (
              <label className="upRolesSearch">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-4-4" />
                </svg>
                <input
                  type="search"
                  value={schoolSearch}
                  onChange={event => this.setState({ schoolSearch: event.target.value })}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      this.setState({ schoolAppliedSearch: schoolSearch });
                    }
                  }}
                  placeholder="Search schools"
                  aria-label="Search roles by school"
                />
              </label>
            )}
          </header>
          <div className="upProfileCardBody upProfileTableCard">
            {!isPermissionsExist ? (
              <div className="upRolesEmpty">
                You don't have any roles right now. Please request one via the{' '}
                <button className="upProfileActionLink" type="button" onClick={this.onRequestsTabClick}>
                  Requests tab
                </button>
                .
              </div>
            ) : visiblePermissions.length === 0 ? (
              <div className="upRolesEmpty upRolesEmptySearch">
                No schools match “{schoolAppliedSearch}”. Try another part of the school name.
              </div>
            ) : (
              <table className="upProfileTable">
                <thead>
                  <tr>
                    <th>School</th>
                    <th>Role</th>
                    <th>Comment</th>
                    <th>Details</th>
                    <th className="upRolesActionHead">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePermissions.map(permission => {
                    const { school, comment, preset, id } = permission;
                    const schoolName = propz.get(school, ['name'], 'School');
                    const schoolPic = propz.get(school, ['pic'], '');

                    return (
                      <tr key={`profile_roles_tab_${id}`}>
                        <td className="upRolesSchool">
                          <div className="upRolesSchoolIdentity">
                            <span className="upRolesSchoolLogo">
                              <span aria-hidden="true">{this.getSchoolInitials(schoolName)}</span>
                              {schoolPic && (
                                <img
                                  src={schoolPic}
                                  alt=""
                                  onError={event => {
                                    event.currentTarget.style.display = 'none';
                                  }}
                                />
                              )}
                            </span>
                            <span>{schoolName}</span>
                          </div>
                        </td>
                        <td>
                          <span className={this.getRoleBadgeClass(preset)}>{this.getRoleLabel(preset)}</span>
                        </td>
                        <td className="upRolesComment">{comment || '-'}</td>
                        <td className="upRolesDetails">{this.renderPermissionDetails(permission)}</td>
                        <td>
                          <div className="upRolesActions">
                            <button
                              type="button"
                              className="upProfileActionLink upProfileActionLinkDanger"
                              onClick={() => this.onRevokeClick(permission)}
                            >
                              Revoke
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    );
  }
}
